import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, subDays, format } from "date-fns";

interface DayStat {
  date: string;
  visitors: number;
  pageviews: number;
}

interface HourStat {
  hour: string;
  pageviews: number;
}

interface EntryExitPage {
  entry: string;
  exit: string;
  count: number;
}

export interface AnalyticsData {
  activeNow: number;
  todayVisitors: number;
  todayPageviews: number;
  yesterdayVisitors: number;
  yesterdayPageviews: number;
  weeklyData: DayStat[];
  topPages: { path: string; count: number }[];
  topReferrers: { referrer: string; count: number }[];
  deviceBreakdown: { device: string; count: number }[];
  // New metrics
  bounceRate: number; // percentage
  avgSessionDuration: number; // seconds
  
  peakHours: HourStat[];
  popularShelters: { name: string; path: string; count: number }[];
  leastPopularShelters: { name: string; count: number }[];
  entryExitPages: EntryExitPage[];
  cityHeatmap: { city: string; count: number }[];
  buttonClicks: { name: string; count: number }[];
  recentlyOpenedShelters: { name: string; city: string; time: string }[];
  shelterOpenSources: { source: string; count: number }[];
  contactClicks: { type: string; count: number }[];
  emptySearches: { query: string; count: number }[];
  abandonedForms: number;
  submittedForms: number;
  formsByShelter: { name: string; opened: number; submitted: number }[];
}

const detectDevice = (ua: string): string => {
  if (/mobile|android|iphone|ipod/i.test(ua)) return "Telefon";
  if (/tablet|ipad/i.test(ua)) return "Tablet";
  return "Komputer";
};

const isBot = (ua: string): boolean => {
  if (!ua) return false;
  if (/bot|crawl|spider|slurp|googlebot|bingbot|yandex|baidu|duckduck|facebookexternalhit|twitterbot|linkedinbot|semrush|ahrefs|mj12bot|dotbot|petalbot|bytespider|gptbot|claudebot/i.test(ua)) return true;
  if (/headless|phantomjs|puppeteer|playwright/i.test(ua)) return true;
  const chromeMatch = ua.match(/Chrome\/(\d+)/);
  if (chromeMatch && parseInt(chromeMatch[1], 10) < 120) return true;
  return false;
};

export const useAnalytics = () => {
  return useQuery({
    queryKey: ["analytics"],
    queryFn: async (): Promise<AnalyticsData> => {
      const now = new Date();
      const todayStart = startOfDay(now);
      const yesterdayStart = startOfDay(subDays(now, 1));
      const weekAgo = startOfDay(subDays(now, 6));

      // Active visitors (last_seen within 2 minutes), excluding admin IDs
      const twoMinAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();
      const { count: activeNow } = await supabase
        .from("active_visitors")
        .select("*", { count: "exact", head: true })
        .gte("last_seen", twoMinAgo)
        .not("visitor_id", "in", '("eeb66f2e-7c23-4bfa-8aaa-d157dca2ca8b","d6d0ccd7-275c-487b-a4d8-1e5015648d1e","980d77c6-80bb-4f59-ae95-ce346571f976","a5395a39-8b3b-4135-b821-ab7db342badc","8c76f383-0a15-4bbd-a17d-0474169ea076","d3dcbb9d-9a26-4ef6-b14f-87bb2a247a78")');

      // Fetch all visits from last 7 days
      const { data: visits, error } = await supabase
        .from("page_visits")
        .select("*")
        .gte("created_at", weekAgo.toISOString())
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Filter out lovable.dev traffic, admin visitors, and lovableproject.com referrers
      const EXCLUDED_VISITOR_IDS = [
        "eeb66f2e-7c23-4bfa-8aaa-d157dca2ca8b",
        "d6d0ccd7-275c-487b-a4d8-1e5015648d1e",
        "980d77c6-80bb-4f59-ae95-ce346571f976",
        "a5395a39-8b3b-4135-b821-ab7db342badc",
        "8c76f383-0a15-4bbd-a17d-0474169ea076",
        "d3dcbb9d-9a26-4ef6-b14f-87bb2a247a78",
      ];
      const EXCLUDED_REFERRERS = ["lovable.dev", "lovableproject.com"];

      const rows = (visits || []).filter(r => {
        if (EXCLUDED_VISITOR_IDS.includes(r.visitor_id)) return false;
        if (r.referrer && EXCLUDED_REFERRERS.some(ref => r.referrer!.includes(ref))) return false;
        if (isBot(r.user_agent || "")) return false;
        return true;
      });

      // Fetch button click events from last 7 days (excluding admin)
      const { data: events } = await (supabase.from("analytics_events") as any)
        .select("*")
        .gte("created_at", weekAgo.toISOString())
        .not("visitor_id", "in", '("eeb66f2e-7c23-4bfa-8aaa-d157dca2ca8b","d6d0ccd7-275c-487b-a4d8-1e5015648d1e","980d77c6-80bb-4f59-ae95-ce346571f976","a5395a39-8b3b-4135-b821-ab7db342badc","8c76f383-0a15-4bbd-a17d-0474169ea076","d3dcbb9d-9a26-4ef6-b14f-87bb2a247a78")');

      // Fetch shelters for city heatmap & popular shelters
      const { data: shelters } = await supabase
        .from("shelters")
        .select("id, name, city");

      // Today
      const todayRows = rows.filter(r => new Date(r.created_at) >= todayStart);
      const todayVisitors = new Set(todayRows.map(r => r.visitor_id)).size;
      const todayPageviews = todayRows.length;

      // Yesterday
      const yesterdayRows = rows.filter(r => {
        const d = new Date(r.created_at);
        return d >= yesterdayStart && d < todayStart;
      });
      const yesterdayVisitors = new Set(yesterdayRows.map(r => r.visitor_id)).size;
      const yesterdayPageviews = yesterdayRows.length;

      // Weekly data
      const weeklyData: DayStat[] = [];
      for (let i = 6; i >= 0; i--) {
        const dayStart = startOfDay(subDays(now, i));
        const dayEnd = startOfDay(subDays(now, i - 1));
        const effectiveRows = i === 0
          ? rows.filter(r => new Date(r.created_at) >= dayStart)
          : rows.filter(r => {
              const d = new Date(r.created_at);
              return d >= dayStart && d < dayEnd;
            });
        weeklyData.push({
          date: format(dayStart, "dd.MM"),
          visitors: new Set(effectiveRows.map(r => r.visitor_id)).size,
          pageviews: effectiveRows.length,
        });
      }

      // Top pages
      const pageCounts: Record<string, number> = {};
      rows.forEach(r => { pageCounts[r.path] = (pageCounts[r.path] || 0) + 1; });
      const topPages = Object.entries(pageCounts)
        .map(([path, count]) => ({ path, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Top referrers — unique visitors per referrer (not pageviews)
      const refVisitors: Record<string, Set<string>> = {};
      rows.forEach(r => {
        let ref = "Bezpośredni";
        if (r.referrer) {
          try {
            ref = new URL(r.referrer).hostname || "Bezpośredni";
          } catch {
            ref = r.referrer;
          }
        }
        if (!refVisitors[ref]) refVisitors[ref] = new Set();
        refVisitors[ref].add(r.visitor_id);
      });
      const topReferrers = Object.entries(refVisitors)
        .map(([referrer, visitors]) => ({ referrer, count: visitors.size }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Device breakdown
      const deviceCounts: Record<string, number> = {};
      const uniqueVisitors = new Map<string, string>();
      rows.forEach(r => {
        if (!uniqueVisitors.has(r.visitor_id)) {
          uniqueVisitors.set(r.visitor_id, r.user_agent || "");
        }
      });
      uniqueVisitors.forEach(ua => {
        const device = detectDevice(ua);
        deviceCounts[device] = (deviceCounts[device] || 0) + 1;
      });
      const deviceBreakdown = Object.entries(deviceCounts)
        .map(([device, count]) => ({ device, count }))
        .sort((a, b) => b.count - a.count);

      // === NEW METRICS ===

      // 1. Bounce rate — visitors with only 1 pageview (7 days)
      const visitorPageCounts = new Map<string, number>();
      rows.forEach(r => {
        visitorPageCounts.set(r.visitor_id, (visitorPageCounts.get(r.visitor_id) || 0) + 1);
      });
      const totalVisitorsWeek = visitorPageCounts.size;
      const bouncedVisitors = Array.from(visitorPageCounts.values()).filter(c => c === 1).length;
      const bounceRate = totalVisitorsWeek > 0 ? Math.round((bouncedVisitors / totalVisitorsWeek) * 100) : 0;

      // 2. Average session duration (seconds) — diff between first and last visit per visitor (7 days)
      const visitorTimes = new Map<string, { first: number; last: number }>();
      rows.forEach(r => {
        const t = new Date(r.created_at).getTime();
        const existing = visitorTimes.get(r.visitor_id);
        if (!existing) {
          visitorTimes.set(r.visitor_id, { first: t, last: t });
        } else {
          if (t < existing.first) existing.first = t;
          if (t > existing.last) existing.last = t;
        }
      });
      let totalDuration = 0;
      let sessionsWithDuration = 0;
      visitorTimes.forEach(({ first, last }) => {
        const dur = (last - first) / 1000;
        if (dur > 0) {
          totalDuration += dur;
          sessionsWithDuration++;
        }
      });
      const avgSessionDuration = sessionsWithDuration > 0 ? Math.round(totalDuration / sessionsWithDuration) : 0;

      // 4. Peak hours — pageviews by hour of day (last 7 days)
      const hourCounts: Record<number, number> = {};
      rows.forEach(r => {
        const hour = new Date(r.created_at).getHours();
        hourCounts[hour] = (hourCounts[hour] || 0) + 1;
      });
      const peakHours: HourStat[] = [];
      for (let h = 0; h < 24; h++) {
        peakHours.push({ hour: `${h.toString().padStart(2, "0")}:00`, pageviews: hourCounts[h] || 0 });
      }

      // 5. Popular shelters — based on "Otworzono schronisko" events
      const shelterMap = new Map((shelters || []).map(s => [s.id, s]));
      const shelterVisits: Record<string, { name: string; path: string; count: number }> = {};
      const shelterOpenEvents = (events || []).filter((e: any) => e.event_name === "Otworzono schronisko");
      shelterOpenEvents.forEach((e: any) => {
        const id = e.event_data?.shelter_id;
        if (id && shelterMap.has(id)) {
          if (!shelterVisits[id]) {
            shelterVisits[id] = { name: e.event_data.shelter_name || shelterMap.get(id)!.name, path: e.path, count: 0 };
          }
          shelterVisits[id].count++;
        }
      });
      const allShelterVisits = Object.values(shelterVisits);
      const popularShelters = [...allShelterVisits]
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Include shelters with 0 opens
      const shelterVisitMap = new Map(allShelterVisits.map(s => [s.name, s.count]));
      const allSheltersWithCounts = (shelters || []).map(s => ({
        name: s.name,
        count: shelterVisitMap.get(s.name) || 0,
      }));
      const leastPopularShelters = allSheltersWithCounts
        .sort((a, b) => a.count - b.count)
        .slice(0, 10);

      // 6. Entry → Exit pages per visitor
      const visitorPages = new Map<string, { entry: string; exit: string }>();
      rows.forEach(r => {
        if (!visitorPages.has(r.visitor_id)) {
          visitorPages.set(r.visitor_id, { entry: r.path, exit: r.path });
        } else {
          visitorPages.get(r.visitor_id)!.exit = r.path;
        }
      });
      const entryExitCounts = new Map<string, number>();
      visitorPages.forEach(({ entry, exit }) => {
        const key = `${entry}|||${exit}`;
        entryExitCounts.set(key, (entryExitCounts.get(key) || 0) + 1);
      });
      const entryExitPages: EntryExitPage[] = Array.from(entryExitCounts.entries())
        .map(([key, count]) => {
          const [entry, exit] = key.split("|||");
          return { entry, exit, count };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 7. City heatmap — based on "Otworzono schronisko" events
      const cityCounts: Record<string, number> = {};
      shelterOpenEvents.forEach((e: any) => {
        const city = e.event_data?.shelter_city;
        const shelterId = e.event_data?.shelter_id;
        if (city) {
          cityCounts[city] = (cityCounts[city] || 0) + 1;
        } else if (shelterId && shelterMap.has(shelterId)) {
          const shelter = shelterMap.get(shelterId)!;
          cityCounts[shelter.city] = (cityCounts[shelter.city] || 0) + 1;
        }
      });
      const cityHeatmap = Object.entries(cityCounts)
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 8. Button clicks — include filter details from event_data
      const clickCounts: Record<string, number> = {};
      (events || []).forEach((e: any) => {
        if (e.event_name) {
          let label = e.event_name;
          // For filter events, append the specific filter/value
          if (e.event_name === "Filtr" && e.event_data?.filter) {
            label = `Filtr: ${e.event_data.filter} → ${e.event_data.enabled ? "ON" : "OFF"}`;
          } else if (e.event_name === "Filtr miasto" && e.event_data?.city) {
            label = `Filtr miasto: ${e.event_data.city}`;
          }
          clickCounts[label] = (clickCounts[label] || 0) + 1;
        }
      });
      const buttonClicks = Object.entries(clickCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);

      // 9. Recently opened shelters — last 5 unique shelters opened
      const seenShelters = new Set<string>();
      const recentlyOpenedShelters: { name: string; city: string; time: string }[] = [];
      const sortedShelterEvents = [...shelterOpenEvents].sort((a: any, b: any) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      for (const e of sortedShelterEvents as any[]) {
        const id = e.event_data?.shelter_id;
        const name = e.event_data?.shelter_name || (id && shelterMap.has(id) ? shelterMap.get(id)!.name : null);
        const city = e.event_data?.shelter_city || (id && shelterMap.has(id) ? shelterMap.get(id)!.city : "");
        if (name && !seenShelters.has(name)) {
          seenShelters.add(name);
          recentlyOpenedShelters.push({ name, city, time: e.created_at });
          if (recentlyOpenedShelters.length >= 10) break;
        }
      }

      // 10. Shelter open sources — breakdown by source (list/map/search)
      const sourceCounts: Record<string, number> = {};
      const SOURCE_LABELS: Record<string, string> = {
        list: "Lista",
        map: "Mapa",
        search: "Wyszukiwarka",
        unknown: "Nieznane",
      };
      shelterOpenEvents.forEach((e: any) => {
        const raw = e.event_data?.source || "unknown";
        const label = SOURCE_LABELS[raw] || raw;
        sourceCounts[label] = (sourceCounts[label] || 0) + 1;
      });
      const shelterOpenSources = Object.entries(sourceCounts)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);

      // 11. Contact clicks — Telefon, Email, Strona www
      const contactTypes = ["Telefon", "Email", "Strona www"];
      const contactClickCounts: Record<string, number> = {};
      (events || []).forEach((e: any) => {
        if (contactTypes.includes(e.event_name)) {
          contactClickCounts[e.event_name] = (contactClickCounts[e.event_name] || 0) + 1;
        }
      });
      const contactClicks = contactTypes
        .map(type => ({ type, count: contactClickCounts[type] || 0 }))
        .filter(c => c.count > 0);

      // 12. Empty searches — "Wyszukiwanie bez wyników"
      const emptySearchCounts: Record<string, number> = {};
      (events || []).filter((e: any) => e.event_name === "Wyszukiwanie bez wyników").forEach((e: any) => {
        const query = e.event_data?.query;
        if (query) {
          emptySearchCounts[query] = (emptySearchCounts[query] || 0) + 1;
        }
      });
      const emptySearches = Object.entries(emptySearchCounts)
        .map(([query, count]) => ({ query, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 13. Abandoned forms — deduplicate per visitor+shelter (React StrictMode fires effects twice)
      const formOpenEvents = (events || []).filter((e: any) => e.event_name === "Formularz otwarty");
      const formSubmitEvents = (events || []).filter((e: any) => e.event_name === "Formularz wysłany");
      
      // Deduplicate opens: count unique visitor+shelter+type combos (within 30s window)
      const dedupeKey = (e: any) => `${e.visitor_id}|${e.event_data?.shelter_id || 'new'}|${e.event_data?.type || ''}`;
      const seenFormOpens = new Map<string, any>();
      formOpenEvents.forEach((e: any) => {
        const key = dedupeKey(e);
        const existing = seenFormOpens.get(key);
        if (!existing) {
          seenFormOpens.set(key, e);
        }
        // Skip duplicates within 30s of first occurrence
      });
      const dedupedOpens = Array.from(seenFormOpens.values());
      
      const formOpens = dedupedOpens.length;
      const submittedForms = formSubmitEvents.length;
      const abandonedForms = Math.max(0, formOpens - submittedForms);

      // Per-shelter form stats (using deduped opens)
      const formShelterOpens: Record<string, { name: string; opened: number; submitted: number }> = {};
      dedupedOpens.forEach((e: any) => {
        const sid = e.event_data?.shelter_id;
        if (!sid) return;
        const name = shelterMap.has(sid) ? shelterMap.get(sid)!.name : (e.event_data?.shelter_name || sid);
        if (!formShelterOpens[sid]) formShelterOpens[sid] = { name, opened: 0, submitted: 0 };
        formShelterOpens[sid].opened++;
      });
      formSubmitEvents.forEach((e: any) => {
        const sid = e.event_data?.shelter_id;
        if (!sid) return;
        const name = shelterMap.has(sid) ? shelterMap.get(sid)!.name : (e.event_data?.shelter_name || sid);
        if (!formShelterOpens[sid]) formShelterOpens[sid] = { name, opened: 0, submitted: 0 };
        formShelterOpens[sid].submitted++;
      });
      const formsByShelter = Object.values(formShelterOpens)
        .sort((a, b) => (b.opened + b.submitted) - (a.opened + a.submitted))
        .slice(0, 10);

      return {
        activeNow: activeNow ?? 0,
        todayVisitors,
        todayPageviews,
        yesterdayVisitors,
        yesterdayPageviews,
        weeklyData,
        topPages,
        topReferrers,
        deviceBreakdown,
        bounceRate,
        avgSessionDuration,
        
        peakHours,
        popularShelters,
        leastPopularShelters,
        entryExitPages,
        cityHeatmap,
        buttonClicks,
        recentlyOpenedShelters,
        shelterOpenSources,
        contactClicks,
        emptySearches,
        abandonedForms,
        submittedForms,
        formsByShelter,
      };
    },
    refetchInterval: 60_000,
  });
};
